<template>
  <div ref="slider" :class="classes" @click="$_sliderClick">
    <div :class="[prefixClass + '__wrap']">
      <template v-if="range">
        <div :class="[prefixClass + '__handle']"
             :style="{left: firstPosition + '%'}"
             @mousedown="$_onFirstHandleMousedown">
          <t-tooltip
            ref="tooltip"
            :controlled="firstDragging"
            :content="tipFormat(currentValue[0])"
            :disabled="tipDisabled"
            :always="showTip === 'always'"
            placement="top">
            <a :class="firstButtonClasses" href="javascript:void(0)"></a>
          </t-tooltip>
        </div>
        <div :class="[prefixClass + '__handle']"
             :style="{left: secondPosition + '%'}"
             @mousedown="$_onSecondHandleMousedown">
          <t-tooltip
            ref="tooltipSecond"
            :controlled="secondDragging"
            :content="tipFormat(currentValue[1])"
            :disabled="tipDisabled"
            :always="showTip === 'always'"
            placement="top">
            <a :class="secondButtonClasses" href="javascript:void(0)"></a>
          </t-tooltip>
        </div>
      </template>
      <template v-else>
        <div :class="[prefixClass + '__handle']"
             :style="{left: singlePosition + '%'}"
             @mousedown="$_onHandleMousedown">
          <t-tooltip
            ref="tooltip"
            :controlled="dragging"
            :content="tipFormat(currentValue)"
            :disabled="tipDisabled"
            :always="showTip === 'always'"
            placement="top">
            <a :class="buttonClasses" href="javascript:void(0)"></a>
          </t-tooltip>
        </div>
      </template>
      <div :class="[prefixClass + '__track']" :style="barStyle" @click.self="$_sliderClick"></div>
      <div :class="[prefixClass + '__step']" @click.self="$_sliderClick"></div>
      <div v-if="showStops" :class="[prefixClass + '__mark']">
        <div v-for="(item,index) in stops"
             :key="index"
             :class="[prefixClass + '__mark-stop']"
             :style="{ 'left': item.width + '%' }"
             @click.self="$_sliderClick">
          <span v-if="dataUnit && index==0" :class="[prefixClass + '__mark-stop-label']" v-html="dataUnit"></span>
          <span :class="[prefixClass + '__mark-stop-value']" v-html="item.label"></span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { PREFIX_CLASS } from '../../utils/constants'
import { getStyle } from '../../utils/assist'
import Emitter from '../../mixins/emitter'
import Locale from '../../mixins/locale'

const PREFIX_SLIDER_CLASS = `${PREFIX_CLASS}slider`

export default {
  name: 'Slider',
  mixins: [Emitter, Locale],
  props: {
    dataUnit: String,
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    step: {
      type: Number,
      default: 1
    },
    range: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Number, Array],
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showStops: {
      type: Boolean,
      default: false
    },
    tipFormat: {
      type: Function,
      default(val) {
        return val
      }
    },
    showTip: {
      type: String,
      default: 'hover',
      validator(value) {
        return ['hover', 'always', 'never'].includes(value)
      }
    },
    size: {
      type: String,
      default: '',
      validator(value) {
        return ['lg', 'sm', ''].includes(value)
      }
    },
    color: {
      type: String,
      default: '',
      validator(value) {
        return ['success', 'warning', 'info', 'danger', ''].includes(value)
      }
    }
  },
  data() {
    return {
      prefixClass: PREFIX_SLIDER_CLASS,
      currentValue: this.value,
      dragging: false,
      firstDragging: false,
      secondDragging: false,
      startX: 0,
      currentX: 0,
      startPos: 0,
      newPos: null,
      oldSingleValue: this.value,
      singlePosition: 0,
      firstPosition: 0,
      secondPosition: 0
    }
  },
  computed: {
    classes() {
      return [
        `${PREFIX_SLIDER_CLASS}`,
        {
          [`${PREFIX_SLIDER_CLASS}--input`]: this.showInput && !this.range,
          [`${PREFIX_SLIDER_CLASS}--range`]: this.range,
          [`${PREFIX_SLIDER_CLASS}--disabled`]: this.disabled,
          [`${PREFIX_SLIDER_CLASS}--${this.size}`]: this.size,
          [`${PREFIX_SLIDER_CLASS}--${this.color}`]: this.color,
          [`${PREFIX_SLIDER_CLASS}--dragging`]:
            this.dragging || this.firstDragging || this.secondDragging
        }
      ]
    },
    buttonClasses() {
      return [
        `${PREFIX_SLIDER_CLASS}__handle-button`,
        {
          [`${PREFIX_SLIDER_CLASS}__handle-button--dragging`]: this.dragging
        }
      ]
    },
    firstButtonClasses() {
      return [
        `${PREFIX_SLIDER_CLASS}__handle-button`,
        {
          [`${PREFIX_SLIDER_CLASS}__handle-button--dragging`]: this
            .firstDragging
        }
      ]
    },
    secondButtonClasses() {
      return [
        `${PREFIX_SLIDER_CLASS}__handle-button`,
        {
          [`${PREFIX_SLIDER_CLASS}__handle-button--dragging`]: this
            .secondDragging
        }
      ]
    },
    barStyle() {
      let style

      if (this.range) {
        style = {
          width:
            (this.currentValue[1] - this.currentValue[0]) /
              (this.max - this.min) *
              100 +
            '%',
          left:
            (this.currentValue[0] - this.min) / (this.max - this.min) * 100 +
            '%'
        }
      } else {
        style = {
          width:
            (this.currentValue - this.min) / (this.max - this.min) * 100 + '%'
        }
      }

      return style
    },
    stops() {
      let stopCount = (this.max - this.min) / this.step
      let result = []
      let stepWidth = 100 * this.step / (this.max - this.min)
      /**
       * 根据step来做判断保留几位小数
       */
      let start = String(this.step).indexOf('.') + 1
      let end = String(this.step).length
      for (let i = 0; i < stopCount + 1; i++) {
        result.push({
          label: Math.round(i * (this.step * Math.pow(10, end - start))) / Math.pow(10, end - start),
          width: i * stepWidth
        })
      }
      return result
    },
    sliderWidth() {
      return parseInt(getStyle(this.$refs.slider, 'width'), 10)
    },
    tipDisabled() {
      let tipFormat = this.range ? this.tipFormat(this.currentValue[0]) : this.tipFormat(this.currentValue)
      return (
        tipFormat === null ||
        this.showTip === 'never'
      )
    }
  },
  watch: {
    value(val) {
      this.currentValue = val
      this.$_updateValue(val)
    },
    currentValue(val) {
      this.$nextTick(() => {
        this.$refs.tooltip.updatePopper()
        if (this.range) {
          this.$refs.tooltipSecond.updatePopper()
        }
      })
      this.$emit('input', val)
      this.$emit('on-input', val)
      this.$emit('on-change', this.currentValue)
      this.dispatch('FormItem', 'on-form-change', this.currentValue)
    }
  },
  created() {
    if (this.min && this.max && this.min > this.max) {
      let error = new Error(this.t('t.slider.minError'))
      error.nmae = this.t('t.slider.minErrorName')
      throw error
    }
  },
  mounted() {
    if (this.range) {
      const isArray = Array.isArray(this.currentValue)
      if (
        !isArray ||
        (isArray && this.currentValue.length !== 2) ||
        (isArray &&
          (isNaN(this.currentValue[0]) || isNaN(this.currentValue[1])))
      ) {
        this.currentValue = [this.min, this.max]
      } else {
        this.$_updateValue(this.currentValue, true)
      }
    } else {
      if (typeof this.currentValue !== 'number') {
        this.currentValue = this.min
      }
      this.$_updateValue(this.currentValue)
    }
  },
  methods: {
    $_updateValue(val, init = false) {
      if (this.range) {
        let value = [...val]
        if (init) {
          if (value[0] > value[1]) {
            value = [this.min, this.max]
          }
        } else {
          if (value[0] > value[1]) {
            value[0] = value[1]
          }
        }
        if (value[0] < this.min) {
          value[0] = this.min
        }
        if (value[0] > this.max) {
          value[0] = this.max
        }
        if (value[1] < this.min) {
          value[1] = this.min
        }
        if (value[1] > this.max) {
          value[1] = this.max
        }
        if (this.value[0] === value[0] && this.value[1] === value[1]) {
          this.$_setFirstPosition(this.currentValue[0])
          this.setSecondPosition(this.currentValue[1])
          return
        }

        this.currentValue = value
        this.$_setFirstPosition(this.currentValue[0])
        this.setSecondPosition(this.currentValue[1])
      } else {
        if (val < this.min) {
          this.currentValue = this.min
        }
        if (val > this.max) {
          this.currentValue = this.max
        }
        this.$_setSinglePosition(this.currentValue)
      }
    },
    $_sliderClick(event) {
      if (this.disabled) return
      const currentX = event.clientX
      const sliderOffsetLeft = this.$refs.slider.getBoundingClientRect().left
      const newPos = (currentX - sliderOffsetLeft) / this.sliderWidth * 100
      const _val = this.$_getValue(newPos)
      if (this.range) {
        let type = ''
        if (_val <= this.currentValue[0]) {
          type = 'First'
        } else if (_val >= this.currentValue[1]) {
          type = 'Second'
        } else {
          const _val1 = this.currentValue[1] - _val
          const _val2 = _val - this.currentValue[0]
          if (_val1 >= _val2) {
            type = 'First'
          } else {
            type = 'Second'
          }
        }
        this[`$_change${type}Position`](_val)
      } else {
        this.$_changeSinglePosition(newPos)
      }
    },
    $_onHandleMousedown(event) {
      if (this.disabled) return
      event.preventDefault()
      this.$_singleDragStart(event)
      window.addEventListener('mousemove', this.$_onDragWindowMousemove)
      window.addEventListener('mouseup', this.$_onDragWindowMouseup)
    },
    $_singleDragStart(event) {
      this.dragging = true
      this.startX = event.clientX
      this.startPos = parseInt(this.singlePosition, 10)
    },
    $_onDragWindowMousemove(event) {
      if (this.dragging) {
        this.$refs.tooltip.visible = true
        this.currentX = event.clientX
        const diff = (this.currentX - this.startX) / this.sliderWidth * 100
        this.newPos = this.startPos + diff
        this.$_changeSinglePosition(this.newPos)
      }
    },
    $_onDragWindowMouseup() {
      if (this.dragging) {
        this.dragging = false
        this.$refs.tooltip.visible = false
        this.$_changeSinglePosition(this.newPos)
        window.removeEventListener('mousemove', this.$_onDragWindowMousemove)
        window.removeEventListener('mouseup', this.$_onDragWindowMouseup)
      }
    },
    $_changeSinglePosition(newPos) {
      if (newPos < 0) {
        newPos = 0
      } else if (newPos > 100) {
        newPos = 100
      }
      let max = this.max
      let min = this.min
      let step = this.step
      if (this.step < 1) {
        max = max * 100
        min = min * 100
        step = step * 100
      }
      const lengthPerStep = 100 / ((max - min) / step)
      const steps = Math.round(newPos / lengthPerStep)

      let position = Math.round(
        steps * lengthPerStep * (max - min) * 0.01 + this.min
      )
      if (this.step < 1) {
        this.currentValue = position / 100
      } else {
        this.currentValue = position
      }
      this.$_setSinglePosition(this.currentValue)
      if (!this.dragging) {
        if (this.currentValue !== this.oldSingleValue) {
          this.$emit('on-change', this.currentValue)
          this.dispatch('FormItem', 'on-form-change', this.currentValue)
          this.oldSingleValue = this.currentValue
        }
      }
    },
    $_setSinglePosition(val) { // val 为value值
      this.singlePosition = (val - this.min) / (this.max - this.min) * 100
    },
    $_onFirstHandleMousedown(event) {
      if (this.disabled) return
      event.preventDefault()
      this.$_firstDragStart(event)
      window.addEventListener('mousemove', this.$_onFirstDragWindowMousemove)
      window.addEventListener('mouseup', this.$_onFirstDragWindowMouseup)
    },
    $_firstDragStart(event) {
      this.firstDragging = true
      this.startX = event.clientX
      this.startPos = parseInt(this.firstPosition, 10)
      this.endVal = this.currentValue[1]
    },
    $_onFirstDragWindowMousemove(event) {
      if (this.firstDragging) {
        this.$refs.tooltip.visible = true
        this.currentX = event.clientX
        const diff = (this.currentX - this.startX) / this.sliderWidth * 100
        this.newPos = this.startPos + diff
        const _val = this.$_getValue(this.newPos)
        if (_val <= this.endVal) {
          this.$_changeFirstPosition(_val)
          this.$_changeSecondPosition(this.endVal)
        } else {
          this.$_changeFirstPosition(this.endVal)
          this.$_changeSecondPosition(_val)
        }
      }
    },
    $_onFirstDragWindowMouseup() {
      if (this.firstDragging) {
        this.firstDragging = false
        this.$refs.tooltip.visible = false
        window.removeEventListener('mousemove', this.$_onFirstDragWindowMousemove)
        window.removeEventListener('mouseup', this.$_onFirstDragWindowMouseup)
      }
    },
    $_getValue(newPos) {
      if (newPos < 0) {
        newPos = 0
      } else if (newPos > 100) {
        newPos = 100
      }
      const lengthPerStep = 100 / ((this.max - this.min) / this.step)
      const steps = Math.round(newPos / lengthPerStep)
      let max = this.max
      let min = this.min
      if (this.step < 1) {
        max = max * 100
        min = min * 100
      }
      let _val = Math.round(
        steps * lengthPerStep * (max - min) * 0.01 + this.min
      )
      if (this.step < 1) {
        _val = _val / 100
      }
      return _val
    },
    $_changeFirstPosition(newVal) {
      this.currentValue = [newVal, this.currentValue[1]]
      this.$_setFirstPosition(newVal)
    },
    $_setFirstPosition(val) {
      this.firstPosition = (val - this.min) / (this.max - this.min) * 100
    },
    $_onSecondHandleMousedown(event) {
      if (this.disabled) return
      event.preventDefault()
      this.$_secondDragStart(event)
      window.addEventListener('mousemove', this.$_onSecondDragWindowMousemove)
      window.addEventListener('mouseup', this.$_onSecondDragWindowMouseup)
    },
    $_secondDragStart(event) {
      this.secondDragging = true
      this.startX = event.clientX
      this.startPos = parseInt(this.secondPosition, 10)
      this.endVal = this.currentValue[0]
    },
    $_onSecondDragWindowMousemove(event) {
      if (this.secondDragging) {
        this.$refs.tooltipSecond.visible = true
        this.currentX = event.clientX
        const diff = (this.currentX - this.startX) / this.sliderWidth * 100
        this.newPos = this.startPos + diff
        const _val = this.$_getValue(this.newPos)
        if (_val < this.endVal) {
          this.$_changeFirstPosition(_val)
          this.$_changeSecondPosition(this.endVal)
        } else {
          this.$_changeSecondPosition(_val)
          this.$_changeFirstPosition(this.endVal)
        }
      }
    },
    $_onSecondDragWindowMouseup() {
      if (this.secondDragging) {
        this.secondDragging = false
        this.$refs.tooltipSecond.visible = false
        window.removeEventListener('mousemove', this.$_onSecondDragWindowMousemove)
        window.removeEventListener('mouseup', this.$_onSecondDragWindowMouseup)
      }
    },
    $_changeSecondPosition(newVal) {
      this.currentValue = [this.currentValue[0], newVal]
      this.setSecondPosition(newVal)
    },
    setSecondPosition(val) {
      this.secondPosition = (val - this.min) / (this.max - this.min) * 100
    }
  }
}
</script>



// WEBPACK FOOTER //
// slider.vue?c8d1a210